/**
 * @aaac/runtime — public API
 *
 * Runtime bridge for executing agent-contracts workflows on Agent SDKs.
 */

import { createRequire } from "node:module";

const require = createRequire(import.meta.url);
const pkg = require("../package.json") as { version: string };

/** Package version string. */
export const version = pkg.version;

export {
  runTask,
  buildTaskPrompt,
  buildSplitTaskPrompt,
  buildRetryFollowUp,
  buildCandidateAgents,
  renderAgentSystemPrompt,
  renderNavigationIndexPrompt,
  renderAnalyzerExplorationMandatePrompt,
  collectAnalyzerToolIds,
  shouldInjectAnalyzerExplorationMandate,
  isNavigationIndex,
  extractStructuredResult,
  zodSchemaToPromptDescription,
  zodSchemaToYamlExample,
  type CandidateAgent,
  type SdkAdapter,
  type AdapterSendOptions,
  type AgentProgressEvent,
  type AgentExecutionRequest,
  type TaskContext,
  type TaskOutcome,
  type TaskRunOptions,
  type TaskRunResult,
  type RetryDecision,
  type AgentContractLike,
  type TaskContractLike,
  type PromptBuilderOptions,
  type SplitPrompt,
  type CacheConfig,
  type ObserverEmitFn,
  type ExecutionEnvelope,
  type TokenUsage,
} from "./lib/task-runner.js";

export {
  runWorkflow,
  buildGateEvaluationPrompt,
  parseGateEvaluationResponse,
  type SdkAdapterFactory,
  type ModelAwareSdkAdapterFactory,
  type AdapterOrFactory,
  type WorkflowInvocation,
  type HandoffInput,
  type WorkflowRegistries,
  type WorkflowRunOptions,
  type WorkflowResult,
  type StepResult,
  type StepCompleteEvent,
  type WorkflowContractLike,
  type WorkflowStep,
  type DelegateStep,
  type GateStep,
  type EvaluateStep,
  type ConvergenceEnvelopePayload,
  type ConvergenceEnvelope,
  type ReceiveSpec,
} from "./lib/workflow-runner.js";

export {
  WORKFLOW_INPUT_STEP_ID,
  buildWorkflowInputOutput,
  formatWorkflowInputContext,
  stepDependsOnWorkflowInput,
  type WorkflowInputHandoff,
  type WorkflowInputOutput,
} from "./lib/workflow-input.js";

export {
  createExecutionEnvelope,
  emitSessionOpen,
  emitSessionClose,
  emitSubagentOpen,
  emitSubagentClose,
  emitIterationOpen,
  emitIterationClose,
  emitIterationMetric,
  emitToolchainOpen,
  emitToolchainClose,
  emitLlmTurnOpen,
  emitLlmTurnClose,
  emitToolExecOpen,
  emitToolExecClose,
  buildObservabilityEnv,
  OBSERVABILITY_ENV_VARS,
  serializeObserverEmit,
  type EnvelopeParams,
  type InnerSpanOptions,
  type ObservabilityIds,
} from "./lib/observability-bridge.js";

export {
  emitProcessWorkflowOpen,
  emitProcessWorkflowClose,
  emitProcessTaskOpen,
  emitProcessTaskClose,
  emitProcessEdit,
  isFileEditTool,
  type ProcessWorkflowOptions,
  type ProcessTaskOptions,
  type ProcessEditOptions,
} from "./lib/process-axis-bridge.js";

export {
  pluginRegistry,
  PluginRegistry,
  type AgentPlugin,
  type GuardrailResult,
  type PromptBuilderArgs,
} from "./lib/plugin.js";

export {
  createGuardrailHooks,
  createGuardrailHooksFromRules,
  createGuardrailHooksForAgent,
  getBlockingViolations,
  getWarnings,
  hasBlockingViolation,
  type GuardrailCheckFunctions,
  type GuardrailHooks,
  type HookContext,
  type HookResponse,
  type GuardrailCheckResult,
  type GuardrailRule,
  type GuardrailRuleData,
} from "./lib/guardrail-hooks.js";

export {
  BoundResolver,
  getEffectiveGuardrailIds,
  getGuardrailRulesForAgent,
  guardrailContextToRuleData,
  buildPerAgentGuardrails,
  type BoundResolveOptions,
  type ResolvedComponent,
  type ResolvedToolchain,
  type ArtifactBindingConfig,
  deriveToolchainsForAgents,
} from "./lib/bound-resolver.js";

export {
  createRuntime,
  WorkflowBuilder,
  type Runtime,
  type RuntimeConfig,
} from "./lib/builder.js";

export {
  loadPlugins,
  loadPluginsFromPaths,
  loadPluginsFromDirectory,
  type PluginLoadResult,
} from "./lib/plugin-loader.js";

export {
  createModelResolver,
  type ModelClass,
  type ModelResolver,
  type ModelMappingConfig,
  type ModelResolverOptions,
  type ResolvedModel,
} from "./lib/model-resolver.js";

export {
  loadDslContext,
  buildRegistriesFromDsl,
  buildGuardrailRulesFromDsl,
  DslValidationError,
  type LoadDslOptions,
  type RequiredEntities,
  type DslContext,
} from "./lib/dsl-context.js";

export { jsonSchemaToZod } from "./lib/schema-converter.js";

export {
  createProgressSink,
  type ProgressSink,
  type ProgressSinkOptions,
} from "./lib/progress-sink.js";

export type { MemoryRef, MemoryStore } from "./lib/memory-store.js";
export { InMemoryStore } from "./lib/memory-store.js";

export { FileMemoryStore } from "./lib/file-memory-store.js";

export {
  AgentLogWriter,
  type RunLogEvent,
  type AgentLogWriterOptions,
} from "./lib/agent-log-writer.js";

export {
  createLibraryToolRegistry,
  createRegistryFromSdkTools,
  type LibraryToolDefinition,
  type LibraryToolRegistry,
} from "./lib/library-tool-binding.js";

export {
  buildSdkFunctionTools,
  type SdkFunctionTool,
} from "./lib/library-tool-sdk.js";

export {
  createAnalyzerBridge,
  isContextPack,
  renderContextPackPrompt,
  type AnalyzerBridge,
  type AnalyzerBridgeOptions,
  type AnalyzeImpactInput,
  type GenerateContextPackInput,
  type BuildCallGraphInput,
} from "./lib/analyzer-bridge.js";

export {
  createAdapter,
  type CreateAdapterOptions,
  type PermissionMode,
} from "./lib/adapter-factory.js";

export {
  executeTask,
  executeWorkflow,
  type ExecuteOptions,
  type ExecuteTaskOptions,
  type ExecuteWorkflowOptions,
  type TaskHookContext,
  type WorkflowHookContext,
} from "./lib/execute.js";

export {
  createRuntimeBinding,
  type AaacInvocationContext,
  type AaacRuntime,
  type AaacPolicy,
  type AaacObserver,
  type OperationHandler,
  type RuntimeBinding,
  type OperationMemoryRefConfig,
  type MemoryStoreEntry,
} from "./component-binding.js";

export {
  createProjectRuntime,
  findProjectConfig,
  PROJECT_CONFIG_FILENAME,
  type CreateProjectRuntimeOptions,
  type ProjectRuntime,
  type ResolvedAdapterSelection,
} from "./lib/project-runtime.js";

export {
  createComponentScope,
  type ComponentScope,
  type CreateComponentScopeOptions,
} from "./component-scope.js";

export {
  buildNavigationContext,
  type NavigationContextOptions,
  type NavigationContext,
} from "./lib/navigation-context.js";

export {
  resolveArtifacts,
  type ArtifactResolveOptions,
  type ArtifactResolveResult,
} from "./lib/artifact-resolver.js";

export {
  evaluateGuardrail,
  listGuardrails,
  guardStatus,
  type EvaluateGuardrailOptions,
  type EvaluateGuardrailResult,
  type ListGuardrailsOptions,
  type ListGuardrailsResult,
  type GuardrailListEntry,
  type GuardStatusOptions,
  type GuardStatusResult,
} from "./lib/guard-commands.js";

export {
  syncClaude,
  checkClaude,
  parseClaudeConfig,
  type SyncOptions as ClaudeSyncOptions,
  type SyncResult as ClaudeSyncResult,
  type CheckResult as ClaudeCheckResult,
  type FileAction as ClaudeFileAction,
  type ClaudeConfig,
} from "./lib/claude-materialize/index.js";

export {
  executePrompt,
  operationsToTools,
  parseRouterResponse,
  type PromptResult,
  type PromptExecuteOptions,
  type OperationToolDef,
} from "./lib/prompt-executor.js";

export {
  runToolchainChecks,
  ToolchainManifestSchema,
  type ToolchainCheckResult,
  type ToolchainManifest,
  type RunToolchainChecksOptions,
} from "./lib/toolchain-checker.js";
